import {
  CHAT_MAX_LENGTH,
  ChatMessagePayloadSchema,
  DeadlineWarningPayloadSchema,
  LeagueStatePayloadSchema,
  MatchStartPayloadSchema,
  RoundProcessedPayloadSchema,
  RoundProcessingPayloadSchema,
  channelName,
  type LeagueStatePayload,
} from "@legacy-manager/shared";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { contagemRegressiva, numero, useT, type Chave } from "../i18n";
import {
  DetalheDeLigaSchema,
  PaginaDoChatSchema,
  ProntoSchema,
  VisaoDaLigaSchema,
  type DetalheDeLiga,
  type MensagemDoChat,
  type TabelaDaVisao,
  type VisaoDaLiga,
} from "../online/esquemas";
import { mensagemDoErro, useRelogio, useSessao } from "../online/sessao";
import {
  Bar,
  Crest,
  Money,
  MoraleFace,
  Panel,
  PointsRing,
  PositionChip,
  Stars,
  Stat,
  StrengthBadge,
} from "../ui/atoms";
import {
  EscudoCosmetico,
  TituloDeTreinador,
  UniformeCosmetico,
  estiloDeEstadio,
} from "../ui/cosmeticos";
import { Classificacao } from "../ui/Classificacao";
import { EstadoDaConexao, SituacaoDeDecisao } from "../ui/online";
import { Icone } from "../ui/icons";
import { PartidaAoVivo } from "./PartidaAoVivo";

/**
 * Hub da liga online: o `ClubHub` do modo em que o mundo não mora aqui.
 *
 * A diferença que manda em todo o resto: **esta tela não simula nada**. Ela
 * desenha a visão filtrada que o servidor montou para este membro, e só. O
 * elenco alheio chega sem atributos, o potencial não chega nunca, e o que não
 * veio na resposta não existe para o cliente (docs/SERVER.md 5).
 *
 * A segunda diferença é o tempo. No solo a rodada acontece quando o jogador
 * clica; aqui ela acontece na hora marcada, com ou sem ele. Por isso o prazo
 * fica em contagem regressiva no lugar mais visível da tela, e por isso o
 * estado das decisões de cada treinador é informação de primeira classe.
 *
 * O socket acelera, não decide. Quando um evento chega dizendo que algo mudou,
 * a tela relê pela API, que é a única fonte da verdade (docs/WEBSOCKET.md).
 */

type Secao = "elenco" | "tabela" | "noticias" | "treinadores" | "chat";

export function HubOnline({
  leagueId,
  aoSair,
}: {
  leagueId: string;
  aoSair: () => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();

  const [visao, definirVisao] = useState<VisaoDaLiga | null>(null);
  const [liga, definirLiga] = useState<DetalheDeLiga | null>(null);
  const [estadoAoVivo, definirEstadoAoVivo] = useState<LeagueStatePayload | null>(null);
  const [processando, definirProcessando] = useState<number | null>(null);
  const [recado, definirRecado] = useState<string | null>(null);
  /*
   * Aqui o erro NÃO vai para o canto, e é a exceção da regra.
   *
   * Nas outras telas ele é resposta de uma ação e a página continua de pé, então
   * o canto é o lugar. Aqui é a liga inteira que não carregou: a página não tem
   * o que mostrar, e ela se desenha como falha, com tentar de novo e sair. Um
   * recado no canto deixaria a tela vazia sem explicação nenhuma no meio.
   */
  const [erro, definirErro] = useState<string | null>(null);
  const [ocupado, definirOcupado] = useState(false);
  const [secao, definirSecao] = useState<Secao>("elenco");
  const [partida, definirPartida] = useState<string | null>(null);
  const [assistindo, definirAssistindo] = useState<string | null>(null);

  const canal = useMemo(() => channelName("league", leagueId), [leagueId]);

  const carregar = useCallback(async () => {
    try {
      const [novaVisao, novaLiga] = await Promise.all([
        sessao.cliente.pedir(
          { metodo: "GET", caminho: `/v1/leagues/${leagueId}/view` },
          VisaoDaLigaSchema,
        ),
        sessao.cliente.pedir(
          { metodo: "GET", caminho: `/v1/leagues/${leagueId}` },
          DetalheDeLigaSchema,
        ),
      ]);
      definirVisao(novaVisao);
      definirLiga(novaLiga);
      definirErro(null);
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    }
  }, [sessao, leagueId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    sessao.socket.assinar(canal);
    const parar = sessao.socket.ouvir((tipo, carga, deQualCanal) => {
      if (deQualCanal !== canal) return;

      switch (tipo) {
        case "league.state": {
          const lido = LeagueStatePayloadSchema.safeParse(carga);
          if (lido.success) definirEstadoAoVivo(lido.data);
          return;
        }
        case "league.round.processing": {
          const lido = RoundProcessingPayloadSchema.safeParse(carga);
          if (lido.success) definirProcessando(lido.data.round);
          // Rodada nova, partida nova: o jogo da rodada passada deixa de ser o
          // que o botão de assistir oferece.
          definirPartida(null);
          return;
        }
        /*
         * A partida entrou no ar.
         *
         * O aviso chega pelo canal da liga porque é o único que o treinador já
         * está ouvindo quando o jogo começa: o canal da partida nasce agora, e
         * o identificador dele vem aqui dentro. É por isso que a tela abre a
         * transmissão sozinha, em vez de esperar o fim da rodada para oferecer
         * um replay.
         */
        case "match.start": {
          const lido = MatchStartPayloadSchema.safeParse(carga);
          if (!lido.success) return;
          definirPartida(lido.data.matchId);
          definirAssistindo(lido.data.matchId);
          return;
        }
        case "league.round.processed": {
          const lido = RoundProcessedPayloadSchema.safeParse(carga);
          definirProcessando(null);
          if (lido.success) {
            const meu = lido.data.myResult;
            definirRecado(
              meu
                ? t("hubOnline.resultado", {
                    rodada: lido.data.round,
                    casa: meu.home,
                    golsCasa: meu.score[0],
                    golsFora: meu.score[1],
                    fora: meu.away,
                  })
                : t("hubOnline.posicaoAgora", {
                    posicao: lido.data.standingsDelta.myPosition,
                  }),
            );
            /*
             * Reserva para quem chegou depois do apito.
             *
             * Quem estava na liga durante a transmissão já recebeu o
             * identificador no `match.start`, e aquele é o canal que tem os
             * lances. Este aqui é o identificador definitivo da partida, e só
             * entra quando não houve aviso ao vivo nenhum.
             */
            definirPartida((atual) => atual ?? (meu ? meu.matchId : null));
          }
          void carregar();
          return;
        }
        case "league.deadline.warning": {
          const lido = DeadlineWarningPayloadSchema.safeParse(carga);
          if (lido.success) {
            definirRecado(
              t("hubOnline.avisoDePrazo", {
                minutos: lido.data.minutesLeft,
                rodada: lido.data.round,
              }),
            );
          }
          return;
        }
        default:
          // Entrada, saída, presença, pausa e fim mexem na liga inteira, e a
          // liga inteira é lida de uma vez só pela API.
          if (tipo.startsWith("league.")) void carregar();
      }
    });

    return () => {
      parar();
      sessao.socket.desassinar(canal);
    };
  }, [sessao, canal, carregar, t]);

  // O socket pediu para recarregar este canal: houve buraco na sequência e o
  // que está na tela pode não ter acontecido.
  useEffect(() => {
    if (sessao.recarga?.canal === canal) void carregar();
  }, [sessao.recarga, canal, carregar]);

  if (!visao || !liga) {
    return (
      <div
        style={{
          flex: 1,
          display: "grid",
          placeItems: "center",
          gap: 12,
          padding: 40,
          textAlign: "center",
        }}
      >
        <p className="muted" style={{ margin: 0 }}>
          {erro ?? t("online.carregando")}
        </p>
        {erro && (
          <div className="row" style={{ gap: 8 }}>
            <button className="ghost" onClick={() => void carregar()}>
              {t("online.tentarDeNovo")}
            </button>
            <button className="ghost" onClick={aoSair}>
              {t("hubOnline.sair")}
            </button>
          </div>
        )}
      </div>
    );
  }

  const clube = visao.myClub;
  /*
   * O que eu comprei chega junto da lista de membros, já conferido contra o
   * direito no servidor. Se o direito cair, o campo volta nulo na leitura
   * seguinte e o cabeçalho volta a ser o de sempre, sem nada a limpar aqui.
   */
  const meusCosmeticos =
    liga.members.find((membro) => membro.userId === sessao.conta?.usuario.id)?.cosmetics ?? null;
  const estado = estadoAoVivo?.state ?? visao.state;
  const prazo = estadoAoVivo?.deadline ?? visao.deadline;
  const rodada = estadoAoVivo?.round ?? visao.round;

  const nav: Array<{ chave: Secao; rotulo: Chave; icone: ReactNode }> = [
    { chave: "elenco", rotulo: "hubOnline.nav.elenco", icone: <Icone nome="Elenco" /> },
    { chave: "tabela", rotulo: "hubOnline.nav.tabela", icone: <Icone nome="Tabela" /> },
    { chave: "noticias", rotulo: "hubOnline.nav.noticias", icone: <Icone nome="Noticias" /> },
    {
      chave: "treinadores",
      rotulo: "hubOnline.nav.treinadores",
      icone: <Icone nome="Treinadores" />,
    },
    { chave: "chat", rotulo: "hubOnline.nav.chat", icone: <Icone nome="Chat" /> },
  ];

  return (
    <div className="shell" data-round={visao.round} data-season={visao.season}>
      <nav className="sidebar" aria-label={t("hubOnline.navAria")}>
        <div className="sidebar-brand">
          {/* Emblema e nome lado a lado, como no conceito de arte. O emblema é
              decorativo: quem usa leitor de tela ouve o nome do texto ao lado. */}
          <div className="row" style={{ gap: 9 }}>
            <img
              className="emblema"
              src="/arte/emblema.webp"
              alt=""
              width={34}
              height={34}
              style={{ width: 34, height: 34, flexShrink: 0 }}
            />
            <div style={{ fontWeight: 800, letterSpacing: "0.02em", lineHeight: 1.05, fontSize: 15 }}>
              {t("comum.marcaLinha1")}
              <br />
              <span style={{ fontSize: "0.68em", letterSpacing: "0.06em", color: "var(--primary)" }}>{t("comum.marcaLinha2")}</span>
            </div>
          </div>
          <button
            className="ghost"
            onClick={aoSair}
            style={{ marginTop: 8, minHeight: 28, padding: "3px 8px", fontSize: 11 }}
          >
            {t("hubOnline.sair")}
          </button>
        </div>
        <div className="sidebar-nav">
          {nav.map((entrada) => (
            <button
              key={entrada.chave}
              className="nav-item"
              aria-current={secao === entrada.chave ? "page" : undefined}
              onClick={() => definirSecao(entrada.chave)}
            >
              {entrada.icone}
              <span>{t(entrada.rotulo)}</span>
            </button>
          ))}
        </div>
      </nav>

      <div className="main">
        <header className="club-header" style={estiloDeEstadio(meusCosmeticos?.stadium ?? null)}>
          <div className="club-header-identidade">
          {clube ? (
            <>
              {meusCosmeticos?.crest ? (
                <EscudoCosmetico params={meusCosmeticos.crest} short={clube.short} size={54} />
              ) : (
                <Crest colors={clube.colors} short={clube.short} size={54} />
              )}
              {meusCosmeticos?.kit && <UniformeCosmetico params={meusCosmeticos.kit} size={44} />}
              <div style={{ minWidth: 0 }}>
                <h1 style={{ fontSize: 20, letterSpacing: "-0.01em" }}>{clube.name}</h1>
                <div className="row" style={{ gap: 10, marginTop: 4 }}>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {liga.name}
                  </span>
                  <Stars level={clube.reputation} />
                </div>
              </div>
              <div className="header-facts">
                <Stat label={t("hubOnline.caixa")}>
                  <Money value={clube.cash} />
                </Stat>
                {/* A dívida aparece como valor positivo, igual ao painel do
                    clube no modo solo. O sinal negativo fica para o fluxo de
                    caixa, onde ele quer dizer saída. */}
                <Stat label={t("hubOnline.divida")}>
                  <Money value={clube.debt} />
                </Stat>
                <Stat label={t("hubOnline.estadio")}>
                  {t("hubOnline.lugares", { capacidade: numero(clube.stadiumCapacity) })}
                </Stat>
                <Stat label={t("hubOnline.objetivo")}>{clube.boardLabel}</Stat>
              </div>
            </>
          ) : (
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: 20 }}>{liga.name}</h1>
              <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
                {t("hubOnline.semClube")}
              </p>
            </div>
          )}

          <div style={{ textAlign: "right" }}>
            <div className="label-muted">
              {t("hubOnline.temporadaRodada", {
                temporada: visao.season,
                rodada,
                total: visao.totalRounds,
              })}
            </div>
            <Prazo prazo={prazo} />
          </div>
          </div>

          <div className="club-header-cartoes">
            <ProximoJogo visao={visao} />
            <Desempenho visao={visao} />
          </div>
        </header>

        <Avisos
          estado={estado}
          processando={processando}
          recado={recado}
          erro={erro}
          precisaAtualizar={sessao.precisaAtualizar}
          partida={partida}
          aoAssistir={definirAssistindo}
        />

        <div className="body">
          <div style={{ minWidth: 0, display: "grid", gap: 14 }}>
            {/*
              O que pede resposta abre a coluna; o que é leitura fica no
              cabeçalho. Marcar pronto muda a rodada da liga inteira, e isso
              não é um cartão de consulta.
            */}
            <Decisoes
              liga={liga}
              estadoAoVivo={estadoAoVivo}
              ocupado={ocupado}
              aoMarcar={(pronto) => {
                definirOcupado(true);
                void sessao.cliente
                  .pedir(
                    {
                      metodo: "POST",
                      caminho: `/v1/leagues/${leagueId}/ready`,
                      corpo: { ready: pronto },
                    },
                    ProntoSchema,
                  )
                  .then(() => carregar())
                  .catch((causa: unknown) => definirErro(mensagemDoErro(causa)))
                  .finally(() => definirOcupado(false));
              }}
            />

            {secao === "elenco" && <Elenco visao={visao} />}
            {secao === "tabela" && <Tabelas visao={visao} />}
            {secao === "noticias" && <Noticias visao={visao} />}
            {secao === "treinadores" && <Treinadores liga={liga} />}
            {secao === "chat" && <Chat leagueId={leagueId} liga={liga} canal={canal} />}
          </div>

        </div>

        <div className="actionbar">
          <EstadoDaConexao className="conexao" />
          <button className="ghost" disabled={ocupado} onClick={() => void carregar()}>
            {t("hubOnline.atualizar")}
          </button>
        </div>
      </div>

      {/*
        A partida ao vivo nasce daqui porque é aqui que estão os dados que ela
        precisa: os clubes da liga, o meu clube e o meu elenco. Buscá-los de
        novo lá dentro seria pedir ao servidor o que a tela já tem na mão.
      */}
      {assistindo && (
        <PartidaAoVivo
          matchId={assistindo}
          clubes={visao.clubs}
          meuClubeId={clube?.id ?? null}
          elenco={clube?.squad ?? []}
          aoFechar={() => definirAssistindo(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Prazo e avisos
// ---------------------------------------------------------------------------

/** Contagem regressiva do prazo da rodada, no canto do cabeçalho. */
function Prazo({ prazo }: { prazo: string | null }): ReactNode {
  const t = useT();
  const agora = useRelogio(prazo !== null);
  if (!prazo) {
    return (
      <div className="num" style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>
        {t("hubOnline.semPrazo")}
      </div>
    );
  }

  const restante = Date.parse(prazo) - agora;
  const apertado = restante < 3_600_000;
  return (
    <div style={{ marginTop: 2 }}>
      <div className="label-muted">{t("hubOnline.prazoRotulo")}</div>
      <div
        className="num"
        style={{
          fontSize: 16,
          fontWeight: 800,
          color: restante <= 0 ? "var(--danger)" : apertado ? "var(--warning)" : "var(--primary)",
        }}
      >
        {contagemRegressiva(restante)}
      </div>
    </div>
  );
}

function Avisos({
  estado,
  processando,
  recado,
  erro,
  precisaAtualizar,
  partida,
  aoAssistir,
}: {
  estado: string;
  processando: number | null;
  recado: string | null;
  erro: string | null;
  precisaAtualizar: boolean;
  partida: string | null;
  aoAssistir: ((matchId: string) => void) | undefined;
}): ReactNode {
  const t = useT();

  const linhas: Array<{ texto: string; cor: string }> = [];
  if (precisaAtualizar) {
    linhas.push({ texto: t("online.precisaAtualizar"), cor: "var(--danger)" });
  }
  if (erro) linhas.push({ texto: erro, cor: "var(--danger)" });
  if (processando !== null) {
    linhas.push({ texto: t("hubOnline.processando", { rodada: processando }), cor: "var(--warning)" });
  }
  if (estado === "paused") linhas.push({ texto: t("hubOnline.pausada"), cor: "var(--warning)" });
  if (estado === "finished") linhas.push({ texto: t("hubOnline.encerrada"), cor: "var(--text-muted)" });
  if (estado === "stalled") linhas.push({ texto: t("hubOnline.travada"), cor: "var(--danger)" });
  if (recado) linhas.push({ texto: recado, cor: "var(--primary)" });

  if (linhas.length === 0 && !partida) return null;

  return (
    <div
      className="row"
      style={{
        gap: 12,
        flexWrap: "wrap",
        padding: "8px var(--gutter)",
        borderBottom: "1px solid var(--border)",
        background: "var(--surface)",
      }}
    >
      {linhas.map((linha) => (
        <span key={linha.texto} style={{ fontSize: 12, color: linha.cor }}>
          {linha.texto}
        </span>
      ))}
      {partida && aoAssistir && (
        <button
          className="ghost"
          onClick={() => aoAssistir(partida)}
          style={{ minHeight: 28, padding: "2px 10px", fontSize: 11.5, marginLeft: "auto" }}
        >
          {t("hubOnline.assistir")}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seções
// ---------------------------------------------------------------------------

function Elenco({ visao }: { visao: VisaoDaLiga }): ReactNode {
  const t = useT();
  const clube = visao.myClub;

  if (!clube) {
    return (
      <Panel title={t("hubOnline.elenco")}>
        <p className="muted" style={{ margin: 0 }}>
          {t("hubOnline.semClube")}
        </p>
      </Panel>
    );
  }

  return (
    <Panel title={t("hubOnline.elenco")} padded={false}>
      <div className="scroll" style={{ maxHeight: "62vh" }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 34 }}>{t("hubOnline.elencoColunas.numero")}</th>
              <th style={{ width: 52 }}>{t("hubOnline.elencoColunas.pos")}</th>
              <th>{t("hubOnline.elencoColunas.jogador")}</th>
              <th style={{ width: 44 }}>{t("hubOnline.elencoColunas.forca")}</th>
              <th style={{ width: 44 }}>{t("hubOnline.elencoColunas.moral")}</th>
              <th style={{ width: 90 }}>{t("hubOnline.elencoColunas.fisico")}</th>
              <th className="right" style={{ width: 80 }}>
                {t("hubOnline.elencoColunas.salario")}
              </th>
            </tr>
          </thead>
          <tbody>
            {clube.squad.map((jogador) => (
              <tr key={jogador.id} className="player">
                <td className="num muted">{jogador.shirt ?? t("comum.vazio")}</td>
                <td>
                  <PositionChip pos={jogador.pos} />
                </td>
                <td>
                  <div style={{ fontWeight: 600 }}>{jogador.name}</div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {t("hubOnline.linhaSecundaria", {
                      idade: jogador.age,
                      destaques: jogador.highlights.join(", "),
                    })}
                    {jogador.injuryRounds > 0 &&
                      ` · ${t("hubOnline.lesionado", { rodadas: jogador.injuryRounds })}`}
                    {jogador.suspensionRounds > 0 && ` · ${t("hubOnline.suspenso")}`}
                  </div>
                </td>
                <td>
                  <StrengthBadge value={jogador.strength} />
                </td>
                <td>
                  <MoraleFace value={jogador.morale} />
                </td>
                <td>
                  <Bar value={jogador.condition} label={t("comum.condicao")} />
                </td>
                <td className="right">
                  <Money value={jogador.salary} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function Tabelas({ visao }: { visao: VisaoDaLiga }): ReactNode {
  const t = useT();

  if (visao.standings.length === 0) {
    return (
      <Panel title={t("hubOnline.tabela")}>
        <p className="muted" style={{ margin: 0 }}>
          {t("hubOnline.semTabela")}
        </p>
      </Panel>
    );
  }

  const nomeDoClube = new Map(visao.clubs.map((clube) => [clube.id, clube.name]));
  const meu = visao.myClub?.id ?? null;

  return (
    <>
      {visao.standings.map((tabela) => (
        /*
         * A mesma tabela da carreira, e não outra escrita aqui.
         *
         * O servidor devolve `gf` e `ga` e identifica o clube por id, enquanto
         * o motor devolve nome e `isMine` prontos. A tradução mora aqui, e o
         * desenho mora em `ui/Classificacao`.
         */
        <Classificacao
          key={tabela.competitionId}
          titulo={tabela.competitionName}
          alturaMaxima="50vh"
          linhas={tabela.rows.map((linha) => ({
            clubId: linha.clubId,
            posicao: linha.position,
            nome: nomeDoClube.get(linha.clubId) ?? linha.clubId,
            jogos: linha.played,
            vitorias: linha.wins,
            empates: linha.draws,
            derrotas: linha.losses,
            golsPro: linha.gf,
            golsContra: linha.ga,
            pontos: linha.points,
            minha: linha.clubId === meu,
          }))}
        />
      ))}
    </>
  );
}

function Noticias({ visao }: { visao: VisaoDaLiga }): ReactNode {
  const t = useT();
  const recentes = [...visao.news].reverse();

  return (
    <Panel title={t("hubOnline.noticias")}>
      {recentes.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          {t("hubOnline.semNoticias")}
        </p>
      )}
      <div className="grid" style={{ gap: 12 }}>
        {recentes.map((noticia) => (
          <article key={noticia.id}>
            <div className="label-muted">
              {t("hubOnline.noticiaRodada", { rodada: noticia.round })}
            </div>
            <h3 style={{ fontSize: 13.5, marginTop: 2 }}>{noticia.title}</h3>
            <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
              {noticia.body}
            </p>
          </article>
        ))}
      </div>
    </Panel>
  );
}

function Treinadores({ liga }: { liga: DetalheDeLiga }): ReactNode {
  const t = useT();
  return (
    <Panel title={t("hubOnline.treinadores")}>
      <div className="grid" style={{ gap: 8 }}>
        {liga.members.map((membro) => (
          <div key={membro.userId} className="row" style={{ gap: 10 }}>
            <span
              aria-hidden="true"
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                flexShrink: 0,
                background: membro.online ? "var(--success)" : "var(--text-dim)",
              }}
            />
            {membro.cosmetics.crest && membro.clubName && (
              <EscudoCosmetico
                params={membro.cosmetics.crest}
                short={membro.clubName.slice(0, 3).toUpperCase()}
                size={24}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600 }}>{membro.displayName}</span>
                {membro.cosmetics.title && <TituloDeTreinador params={membro.cosmetics.title} />}
              </div>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {membro.clubName ?? t("ligas.saguao.semClube")}
              </div>
            </div>
            <span className="label">
              <SituacaoDeDecisao pronto={membro.ready} />
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Coluna da direita
// ---------------------------------------------------------------------------

function minhaLinha(visao: VisaoDaLiga): { tabela: TabelaDaVisao; posicao: number; pontos: number; jogos: number } | null {
  const meu = visao.myClub?.id;
  if (!meu) return null;
  for (const tabela of visao.standings) {
    const linha = tabela.rows.find((candidata) => candidata.clubId === meu);
    if (linha) {
      return { tabela, posicao: linha.position, pontos: linha.points, jogos: linha.played };
    }
  }
  return null;
}

function Desempenho({ visao }: { visao: VisaoDaLiga }): ReactNode {
  const t = useT();
  const linha = minhaLinha(visao);
  if (!linha) return null;

  return (
    <Panel title={linha.tabela.competitionName}>
      <div className="row" style={{ gap: 14 }}>
        <PointsRing points={linha.pontos} played={linha.jogos} />
        <div className="grid" style={{ gap: 8 }}>
          <Stat label={t("hub.posicao")}>{t("hub.posicaoValor", { posicao: linha.posicao })}</Stat>
          <Stat label={t("fimDeTemporada.jogos")}>{linha.jogos}</Stat>
        </div>
      </div>
    </Panel>
  );
}

function Decisoes({
  liga,
  estadoAoVivo,
  ocupado,
  aoMarcar,
}: {
  liga: DetalheDeLiga;
  estadoAoVivo: LeagueStatePayload | null;
  ocupado: boolean;
  aoMarcar: (pronto: boolean) => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const ativos = liga.members.filter((membro) => membro.status === "active");
  const eu = ativos.find((membro) => membro.userId === sessao.conta?.usuario.id);
  const prontos = estadoAoVivo?.readyCount ?? ativos.filter((membro) => membro.ready).length;
  const total = estadoAoVivo?.humanCount ?? ativos.length;

  return (
    <Panel title={t("hubOnline.decisoes")}>
      <div style={{ display: "grid", gap: 10 }}>
        <Stat label={t("hubOnline.prontos", { prontos, total })}>
          <Bar value={total === 0 ? 0 : Math.round((prontos / total) * 100)} />
        </Stat>

        {eu && (
          <button
            className={eu.ready ? "ghost" : "primary"}
            disabled={ocupado || liga.state !== "running"}
            onClick={() => aoMarcar(!eu.ready)}
          >
            {eu.ready ? t("hubOnline.aindaNao") : t("hubOnline.estouPronto")}
          </button>
        )}

        <p className="muted" style={{ margin: 0, fontSize: 11 }}>
          {t("hubOnline.decisoesAjuda")}
        </p>
      </div>
    </Panel>
  );
}

function ProximoJogo({ visao }: { visao: VisaoDaLiga }): ReactNode {
  const t = useT();
  const jogo = visao.nextMatch;
  const meu = visao.myClub?.id ?? null;

  if (!jogo) {
    return (
      <Panel title={t("hubOnline.proximoJogo")}>
        <p className="muted" style={{ margin: 0 }}>
          {t("hubOnline.semJogoMarcado")}
        </p>
      </Panel>
    );
  }

  const casa = visao.clubs.find((clube) => clube.id === jogo.homeClubId);
  const fora = visao.clubs.find((clube) => clube.id === jogo.awayClubId);
  const emCasa = jogo.homeClubId === meu;

  return (
    <Panel title={t("hubOnline.proximoJogo")}>
      <div className="row" style={{ gap: 10, justifyContent: "center" }}>
        {casa && <Crest colors={casa.colors} short={casa.short} size={30} />}
        <span className="label-muted">{t("hubOnline.versus")}</span>
        {fora && <Crest colors={fora.colors} short={fora.short} size={30} />}
      </div>
      <p style={{ margin: "8px 0 0", textAlign: "center", fontSize: 12.5 }}>
        {casa?.name ?? jogo.homeClubId} {t("hubOnline.versus")} {fora?.name ?? jogo.awayClubId}
      </p>
      <p className="label-muted" style={{ margin: "4px 0 0", textAlign: "center" }}>
        {emCasa ? t("hubOnline.emCasa") : t("hubOnline.foraDeCasa")}
      </p>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Chat da liga
// ---------------------------------------------------------------------------

function Chat({
  leagueId,
  liga,
  canal,
}: {
  leagueId: string;
  liga: DetalheDeLiga;
  canal: string;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [mensagens, definirMensagens] = useState<MensagemDoChat[]>([]);
  const [semHistorico, definirSemHistorico] = useState(false);
  const [texto, definirTexto] = useState("");
  const [digitando, definirDigitando] = useState(false);
  const fim = useRef<HTMLDivElement | null>(null);

  const nomes = useMemo(
    () => new Map(liga.members.map((membro) => [membro.userId, membro.displayName])),
    [liga.members],
  );

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const pagina = await sessao.cliente.pedir(
          { metodo: "GET", caminho: `/v1/leagues/${leagueId}/chat?limit=50` },
          PaginaDoChatSchema,
        );
        if (!vivo) return;
        // A página pode vir do mais novo para o mais antigo. A tela lê de cima
        // para baixo, então a ordem é imposta aqui e não presumida.
        definirMensagens(
          [...pagina.items].sort(
            (uma, outra) => Date.parse(uma.createdAt) - Date.parse(outra.createdAt),
          ),
        );
      } catch {
        if (vivo) definirSemHistorico(true);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [sessao, leagueId]);

  useEffect(() => {
    return sessao.socket.ouvir((tipo, carga, deQualCanal) => {
      if (deQualCanal !== canal) return;
      if (tipo === "chat.message") {
        const lido = ChatMessagePayloadSchema.safeParse(carga);
        if (!lido.success) return;
        // Montada campo a campo, e não revalidada: um erro lançado aqui dentro
        // subiria pelo laço de ouvintes do socket e derrubaria os outros.
        const mensagem: MensagemDoChat = {
          id: lido.data.id,
          userId: lido.data.userId,
          kind: lido.data.kind,
          text: lido.data.text,
          createdAt: lido.data.createdAt,
        };
        definirMensagens((atuais) =>
          atuais.some((outra) => outra.id === mensagem.id) ? atuais : [...atuais, mensagem],
        );
        definirDigitando(false);
        return;
      }
      if (tipo === "chat.typing") definirDigitando(true);
    });
  }, [sessao, canal]);

  // O aviso de digitação não tem fim próprio no protocolo: ele some sozinho.
  useEffect(() => {
    if (!digitando) return;
    const id = window.setTimeout(() => definirDigitando(false), 4000);
    return () => window.clearTimeout(id);
  }, [digitando]);

  useEffect(() => {
    fim.current?.scrollIntoView({ block: "end" });
  }, [mensagens.length]);

  const enviar = (evento: FormEvent): void => {
    evento.preventDefault();
    const limpo = texto.trim();
    if (!limpo) return;
    sessao.socket.enviar("chat.send", { leagueId, text: limpo });
    definirTexto("");
  };

  return (
    <Panel title={t("hubOnline.chat")}>
      {semHistorico && (
        <p className="muted" style={{ margin: "0 0 10px", fontSize: 11.5 }}>
          {t("hubOnline.chatSemHistorico")}
        </p>
      )}

      <div
        className="scroll"
        style={{
          maxHeight: "48vh",
          minHeight: 180,
          background: "var(--surface-2)",
          borderRadius: "var(--radius-sm)",
          padding: "8px 10px",
        }}
      >
        {mensagens.length === 0 && (
          <p className="muted" style={{ margin: 0, fontSize: 12 }}>
            {t("hubOnline.chatVazio")}
          </p>
        )}
        {mensagens.map((mensagem) => (
          <div key={mensagem.id} style={{ padding: "3px 0" }}>
            <span
              className="label"
              style={{ color: mensagem.kind === "system" ? "var(--accent)" : "var(--primary)" }}
            >
              {mensagem.kind === "system"
                ? t("hubOnline.chatSistema")
                : (mensagem.displayName ??
                  (mensagem.userId ? nomes.get(mensagem.userId) : null) ??
                  t("hubOnline.chatSistema"))}
            </span>
            <span style={{ fontSize: 12.5, marginLeft: 8 }}>{mensagem.text}</span>
          </div>
        ))}
        <div ref={fim} />
      </div>

      {digitando && (
        <p className="label-muted" style={{ margin: "6px 0 0" }}>
          {t("hubOnline.chatDigitando")}
        </p>
      )}

      <form onSubmit={enviar} className="row" style={{ gap: 8, marginTop: 10 }}>
        <input
          value={texto}
          onChange={(evento) => definirTexto(evento.target.value)}
          placeholder={t("hubOnline.chatCampo")}
          maxLength={CHAT_MAX_LENGTH}
          style={{ flex: 1, minWidth: 0 }}
        />
        <button className="primary" type="submit" disabled={!sessao.aoVivo || !texto.trim()}>
          {t("hubOnline.chatEnviar")}
        </button>
      </form>

      {!sessao.aoVivo && (
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 11 }}>
          {t("hubOnline.chatDesconectado")}
        </p>
      )}
    </Panel>
  );
}
